import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Save, MapPin, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const SEGMENTOS = ['Supermercado', 'Indústria', 'Atacado', 'Varejo', 'Distribuidor', 'Outros'];

const SEG_COLORS = {
  Supermercado: 'bg-blue-100 text-blue-700 border-blue-200',
  Indústria:    'bg-amber-100 text-amber-700 border-amber-200',
  Atacado:      'bg-purple-100 text-purple-700 border-purple-200',
  Varejo:       'bg-emerald-100 text-emerald-700 border-emerald-200',
  Distribuidor: 'bg-teal-100 text-teal-700 border-teal-200',
  Outros:       'bg-slate-100 text-slate-600 border-slate-200',
};

const FORM_INITIAL = {
  nome: '', nome_fantasia: '', cnpj: '', inscricao_estadual: '', segmento: '',
  endereco: '', cep: '', cidade: '', estado: '',
  comprador: '', telefone: '', email: '', isento_ipi: false,
  prazo_pagamento: '', observacoes: '',
};

const inp = "w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const maskCNPJ = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 14);
  if (d.length <= 2)  return d;
  if (d.length <= 5)  return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8)  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12,14)}`;
};

const maskTelefone = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 11);
  if (d.length === 0)  return '';
  if (d.length <= 2)   return `(${d}`;
  if (d.length <= 6)   return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10)  return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7,11)}`;
};

const maskCEP = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0,5)}-${d.slice(5)}`;
};

const ClienteForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState(FORM_INITIAL);
  const [initialData, setInitialData] = useState(FORM_INITIAL);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [condicoes, setCondicoes] = useState([]);
  const [prazoOutra, setPrazoOutra] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [camposAutoFill, setCamposAutoFill] = useState(new Set());

  useEffect(() => {
    api.get('/condicoes-pagamento')
      .then(r => setCondicoes(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditing || condicoes.length === 0 || !initialData.prazo_pagamento) return;
    if (!condicoes.some(c => c.descricao === initialData.prazo_pagamento)) {
      setPrazoOutra(true);
    }
  }, [condicoes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEditing) fetchCliente();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCliente = async () => {
    try {
      const r = await api.get(`/clientes/${id}`);
      const c = r.data;
      const data = {
        nome:               c.nome               || '',
        nome_fantasia:      c.nome_fantasia       || '',
        cnpj:               maskCNPJ(c.cnpj       || ''),
        inscricao_estadual: c.inscricao_estadual  || '',
        segmento:           c.segmento            || '',
        endereco:           c.endereco            || '',
        cep:                maskCEP(c.cep         || ''),
        cidade:             c.cidade              || '',
        estado:             c.estado              || '',
        comprador:          c.comprador           || '',
        telefone:           maskTelefone(c.telefone || ''),
        email:              c.email               || '',
        isento_ipi:         c.isento_ipi          || false,
        prazo_pagamento:    c.prazo_pagamento      || '',
        observacoes:        c.observacoes          || '',
      };
      setFormData(data);
      setInitialData(data);
    } catch {
      toast.error('Cliente não encontrado');
      navigate('/clientes');
    } finally {
      setLoading(false);
    }
  };

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData);

  const handleBack = () => {
    if (isDirty && !window.confirm('Há alterações não salvas. Deseja sair mesmo assim?')) return;
    navigate('/clientes');
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const inpFilled = (field) => camposAutoFill.has(field)
    ? "w-full border-2 border-emerald-400 ring-2 ring-emerald-400/20 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all"
    : inp;

  const buscarCEP = async (cep) => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await r.json();
      if (data.erro) return toast.error('CEP não encontrado');
      setFormData(prev => ({
        ...prev,
        endereco: data.logradouro ? data.logradouro.toUpperCase() : prev.endereco,
        cidade:   data.localidade ? data.localidade.toUpperCase() : prev.cidade,
        estado:   data.uf         ? data.uf.toUpperCase()         : prev.estado,
      }));
    } catch {
      toast.error('Erro ao buscar CEP');
    }
  };

  const buscarCNPJ = async () => {
    const digits = formData.cnpj.replace(/\D/g, '');
    if (digits.length !== 14) return;
    setCnpjLoading(true);
    try {
      const r = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${digits}`);
      if (!r.ok) throw new Error('not_found');
      const data = await r.json();

      const preenchidos = [];
      const next = { ...formData };

      const trySet = (field, value) => {
        if (!formData[field] && value) {
          next[field] = value;
          preenchidos.push(field);
        }
      };

      trySet('nome',         (data.razao_social  || '').toUpperCase());
      trySet('nome_fantasia', (data.nome_fantasia || '').toUpperCase());

      const partes = [data.logradouro, data.numero, data.complemento, data.bairro].filter(Boolean);
      trySet('endereco', partes.join(', ').toUpperCase());

      trySet('cidade',   (data.municipio || '').toUpperCase());
      trySet('estado',   (data.uf        || '').toUpperCase());
      trySet('telefone', maskTelefone(data.ddd_telefone_1 || ''));
      trySet('email',    (data.email     || '').toLowerCase());

      if (preenchidos.length > 0) {
        setFormData(next);
        setCamposAutoFill(new Set(preenchidos));
        toast.success('Dados preenchidos automaticamente!');
        setTimeout(() => setCamposAutoFill(new Set()), 3000);
      } else {
        toast.success('CNPJ encontrado na Receita Federal.');
      }
    } catch {
      toast.error('CNPJ não encontrado na Receita Federal');
    } finally {
      setCnpjLoading(false);
    }
  };

  useEffect(() => {
    const digits = formData.cnpj.replace(/\D/g, '');
    if (!isEditing && digits.length === 14) buscarCNPJ();
  }, [formData.cnpj]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return toast.error('Ação não permitida');
    setSaving(true);
    try {
      const data = {
        ...formData,
        cnpj:     formData.cnpj.replace(/\D/g, ''),
        telefone: formData.telefone.replace(/\D/g, ''),
        cep:      formData.cep.replace(/\D/g, ''),
      };
      if (isEditing) {
        await api.put(`/clientes/${id}`, data);
        toast.success('Cadastro atualizado!');
      } else {
        await api.post('/clientes', data);
        toast.success('Cliente registrado!');
      }
      navigate('/clientes');
    } catch {
      toast.error('Erro ao salvar cliente');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Cliente...</p>
    </div>
  );

  return (
    <form id="cliente-form" onSubmit={handleSubmit} className="min-h-screen bg-[#E9EEF2]">

      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button type="button" onClick={handleBack}
              className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500 hover:text-[#0A3D73] transition-colors">
              <ArrowLeft size={14} /> Voltar
            </button>
            <div className="w-px h-5 bg-slate-200" />
            <div>
              <h1 className="text-base font-black text-slate-900">
                {isEditing
                  ? `Editar — ${formData.nome_fantasia || formData.nome || id}`
                  : 'Novo Cliente'}
              </h1>
              <p className="text-[11px] text-slate-400 font-semibold">Cadastro de Cliente</p>
            </div>
          </div>
          <button
            type="submit"
            form="cliente-form"
            disabled={saving}
            className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-5 py-2.5 rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Save size={14} /> {saving ? 'Salvando...' : 'Salvar Cliente'}
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 max-w-6xl mx-auto">

          {/* ── COL ESQUERDA ── */}
          <div className="space-y-6">

            {/* Card 01 · Identificação */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">01 · Identificação</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={lbl}>Razão Social *</label>
                  <input
                    value={formData.nome}
                    onChange={e => set('nome', e.target.value.toUpperCase())}
                    required className={inpFilled('nome')} placeholder="RAZÃO SOCIAL DA EMPRESA" />
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Nome Fantasia <span className="normal-case font-normal text-slate-300">(opcional — usado na exibição)</span></label>
                  <input
                    value={formData.nome_fantasia}
                    onChange={e => set('nome_fantasia', e.target.value.toUpperCase())}
                    className={inpFilled('nome_fantasia')} placeholder="Ex: SUPERMERCADO MODELO" />
                </div>
                <div>
                  <label className={lbl}>CNPJ / Documento</label>
                  <div className="flex gap-2">
                    <input
                      value={formData.cnpj}
                      onChange={e => set('cnpj', maskCNPJ(e.target.value))}
                      onBlur={buscarCNPJ}
                      className={inp} placeholder="00.000.000/0000-00" />
                    <button
                      type="button"
                      onClick={buscarCNPJ}
                      disabled={cnpjLoading || formData.cnpj.replace(/\D/g, '').length !== 14}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-black text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap shrink-0">
                      {cnpjLoading
                        ? <div className="w-3 h-3 border-2 border-slate-300 border-t-[#0A3D73] rounded-full animate-spin" />
                        : <span>🔍</span>
                      }
                      <span>{cnpjLoading ? 'Buscando...' : 'Buscar CNPJ'}</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label className={lbl}>Inscrição Estadual</label>
                  <input
                    value={formData.inscricao_estadual}
                    onChange={e => set('inscricao_estadual', e.target.value)}
                    className={inp} placeholder="000.000.000" />
                </div>
                <div>
                  <label className={lbl}>Segmento</label>
                  <select
                    value={formData.segmento}
                    onChange={e => set('segmento', e.target.value)}
                    className={`${inp} cursor-pointer`}>
                    <option value="">— Selecione —</option>
                    {SEGMENTOS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Comprador / Contato</label>
                  <input
                    value={formData.comprador}
                    onChange={e => set('comprador', e.target.value.toUpperCase())}
                    className={inp} />
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Regime de IPI</label>
                  <button
                    type="button"
                    onClick={() => set('isento_ipi', !formData.isento_ipi)}
                    className={`flex items-center gap-2 px-4 h-10 rounded-lg border text-xs font-black uppercase transition-all ${
                      formData.isento_ipi
                        ? 'bg-emerald-500 border-emerald-600 text-white'
                        : 'bg-slate-100 border-slate-200 text-slate-500'
                    }`}>
                    <span className={`w-8 h-4 rounded-full relative transition-colors ${formData.isento_ipi ? 'bg-emerald-300' : 'bg-slate-300'}`}>
                      <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all ${formData.isento_ipi ? 'left-4' : 'left-0.5'}`} />
                    </span>
                    {formData.isento_ipi ? 'Isento de IPI ✓' : 'IPI Normal'}
                  </button>
                </div>
              </div>
            </div>

            {/* Card 02 · Localização */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">02 · Localização</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div>
                  <label className={lbl}>CEP</label>
                  <input
                    value={formData.cep}
                    onChange={e => {
                      const masked = maskCEP(e.target.value);
                      set('cep', masked);
                      buscarCEP(masked);
                    }}
                    className={inp} placeholder="00000-000" maxLength={9} />
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Endereço</label>
                  <input
                    value={formData.endereco}
                    onChange={e => set('endereco', e.target.value)}
                    className={inpFilled('endereco')} />
                </div>
                <div>
                  <label className={lbl}>UF</label>
                  <input
                    value={formData.estado}
                    onChange={e => set('estado', e.target.value.toUpperCase())}
                    maxLength={2} className={inpFilled('estado')} placeholder="PE" />
                </div>
                <div>
                  <label className={lbl}>Cidade *</label>
                  <input
                    value={formData.cidade}
                    onChange={e => set('cidade', e.target.value.toUpperCase())}
                    required className={inpFilled('cidade')} />
                </div>
              </div>
            </div>

            {/* Card 03 · Contato */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">03 · Contato</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>E-mail Corporativo</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => set('email', e.target.value)}
                    className={inpFilled('email')} placeholder="contato@empresa.com.br" />
                </div>
                <div>
                  <label className={lbl}>Telefone / WhatsApp</label>
                  <input
                    value={formData.telefone}
                    onChange={e => set('telefone', maskTelefone(e.target.value))}
                    className={inpFilled('telefone')} placeholder="(81) 99999-9999" />
                </div>
              </div>
            </div>

            {/* Card 04 · Condições Comerciais */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">04 · Condições Comerciais</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Prazo de Pagamento</label>
                  <select
                    value={prazoOutra ? '__outra__' : formData.prazo_pagamento}
                    onChange={e => {
                      if (e.target.value === '__outra__') {
                        setPrazoOutra(true);
                        set('prazo_pagamento', '');
                      } else {
                        setPrazoOutra(false);
                        set('prazo_pagamento', e.target.value);
                      }
                    }}
                    className={`${inp} cursor-pointer`}>
                    <option value="">— Selecione —</option>
                    {condicoes.map(c => <option key={c.id} value={c.descricao}>{c.descricao}</option>)}
                    <option value="__outra__">Outra...</option>
                  </select>
                  {prazoOutra && (
                    <input
                      type="text"
                      className={`${inp} mt-1`}
                      placeholder="Digite a condição..."
                      value={formData.prazo_pagamento}
                      onChange={e => set('prazo_pagamento', e.target.value)} />
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Observações</label>
                  <textarea
                    value={formData.observacoes}
                    onChange={e => set('observacoes', e.target.value)}
                    className={`${inp} resize-none`}
                    rows={3}
                    placeholder="Notas internas, restrições de crédito, acordos especiais..." />
                </div>
              </div>
            </div>

          </div>

          {/* ── COL DIREITA ── */}
          <div className="space-y-6">

            {/* Card Resumo */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Resumo</p>
              <div className="space-y-4">
                {(formData.nome_fantasia || formData.nome) ? (
                  <div className="bg-[#EFF6FF] rounded-xl p-4 text-center">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A3D73]/60 mb-1">Nome Fantasia</p>
                    <p className="text-lg font-black text-[#0A3D73] break-words">
                      {formData.nome_fantasia || formData.nome}
                    </p>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-4 text-center border border-dashed border-slate-200">
                    <p className="text-xs font-bold text-slate-400">Preencha a Razão Social</p>
                  </div>
                )}
                {formData.segmento && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Segmento</span>
                    <span className={`px-3 py-1 text-xs font-black border rounded-full ${SEG_COLORS[formData.segmento] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      {formData.segmento}
                    </span>
                  </div>
                )}
                {formData.cnpj && (
                  <div className="flex items-start gap-2.5">
                    <CreditCard size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">CNPJ</p>
                      <p className="text-sm font-black font-mono text-slate-700">{formData.cnpj}</p>
                    </div>
                  </div>
                )}
                {(formData.cidade || formData.estado) && (
                  <div className="flex items-start gap-2.5">
                    <MapPin size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Localização</p>
                      <p className="text-sm font-black text-slate-700">
                        {formData.cidade}{formData.cidade && formData.estado ? ' — ' : ''}{formData.estado}
                      </p>
                    </div>
                  </div>
                )}
                {formData.isento_ipi && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Regime IPI</span>
                    <span className="px-3 py-1 text-xs font-black border rounded-full bg-emerald-100 text-emerald-700 border-emerald-300">
                      Isento IPI
                    </span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </form>
  );
};

export default ClienteForm;
